"use client";

import React, { useState, useRef, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import hasTextMovAnim from "@/lib/animation/hasTextMovAnim";

type Props = {
  vision_label?: string;
  vision_html?: string;
  mission_label?: string;
  mission_html?: string;
};

const VisionMission = ({
  vision_label = "Vision",
  vision_html = "",
  mission_label = "Mission",
  mission_html = "",
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null!);

  const visionContent = { label: vision_label, html: vision_html };
  const missionContent = { label: mission_label, html: mission_html };

  const [active, setActive] = useState<"vision" | "mission">("vision");
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const isMission = active === "mission";
  const currentContent = isMission ? missionContent : visionContent;

  useGSAP(
    () => {
      hasTextMovAnim();
    },
    { scope: containerRef, dependencies: [active] },
  );

  // Measure actual content height whenever active changes
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [active]);

  return (
    <section
      className="w-full section-spacing-top"
      ref={containerRef}
    >
      <div className="inner-container-2">
        <div
          className={`relative rounded-2xl border border-black/10 shadow-xl p-8 md:p-10 lg:p-12 xl:p-16 transition-colors duration-500 ease-in-out has_fade_anim ${
            isMission ? "bg-white text-black" : "bg-black text-white"
          }`}
          data-fade-from="bottom"
          data-delay="0.10"
          data-duration="0.90"
          data-stagger="0.10"
        >
          {/* Content area — height animates smoothly */}
          <div
            className="overflow-hidden transition-[height] duration-500 ease-in-out mb-12 md:mb-16"
            style={{
              height: contentHeight > 0 ? `${contentHeight}px` : "auto",
            }}
          >
            <div
              ref={contentRef}
              key={active}
              className={`has_text_mov_anim text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] leading-relaxed font-light w-full
                [&_b]:font-semibold
                [&_ul]:space-y-4 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:text-center
                [&_li]:!mt-0 [&_li]:!mb-0
                transition-opacity duration-300 text-center
                ${isMission ? "text-black/90" : "text-center text-white/90"}
              `}
              dangerouslySetInnerHTML={{ __html: currentContent.html }}
            />
          </div>

          {/* Toggle Buttons */}
          <div className="flex items-center justify-between">
            {/* Vision Button */}
            <button
              onClick={() => setActive("vision")}
              className={`px-8 py-3 rounded-full text-[14px] md:text-[15px] font-medium border-2 transition-all duration-300 cursor-pointer ${
                !isMission
                  ? "bg-transparent text-white border-white/60 hover:border-white"
                  : "bg-black text-white border-black"
              }`}
            >
              {visionContent.label}
            </button>

            {/* Mission Button */}
            <button
              onClick={() => setActive("mission")}
              className={`px-8 py-3 rounded-full text-[14px] md:text-[15px] font-medium border-2 transition-all duration-300 cursor-pointer ${
                isMission
                  ? "bg-transparent text-black border-black/60 hover:border-black"
                  : "bg-white text-black border-white"
              }`}
            >
              {missionContent.label}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionMission;
